import { observer } from 'mobx-react-lite';
import { taskAriaLabel, taskAriaRowLocationLabel } from './TodoListItem.locstring.json';
import { getRTLSafeKeyCode } from '@uifabric/utilities';
import { FocusZone, FocusZoneDirection, IFocusZone } from '@fluentui/react/lib/FocusZone';
import { Icon } from '@fluentui/react/lib/Icon';
import { KeyCodes, IPoint } from '@fluentui/react/lib/Utilities';
import { AriaProperties, AriaRoles, generateDomPropertiesForAria } from 'owa-accessibility';
import { ControlIcons } from 'owa-control-icons';
import { isFeatureEnabled } from 'owa-feature-flags';
import loc, { format } from 'owa-localize';
import { getAnchorForContextMenu } from 'owa-positioning';
import { Todo } from 'owa-todo-types';
import { isImportant, isTaskComplete } from 'owa-todo-utils';
import * as React from 'react';
import {
    CompleteCheckbox,
    EditableTitle,
    IEditableTitle,
    TodoListItemInfo,
    TodoStarCharm,
    LinkedEmailEntity,
    TodoListInfo,
} from 'owa-todo-views';

const styles = require('./TodoListItem.scss');
const classNames = require('classnames/bind').bind(styles);

export interface TodoListItemCallbacks {
    clickEmail: (emailId: string) => void;
    markTodoComplete: (todoId: string) => void;
    markTodoIncomplete: (todoId: string) => void;
    star: (todoId: string) => void;
    unstar: (todoId: string) => void;
    updateTodoSubject: (todoId: string, subject: string) => void;
    showTodoContextMenu: (todoId: string, anchor: IPoint) => void;
    clickTodo: (todoId: string) => void;
    updateIsEditing: (todoId: string, isEditing: boolean) => void;
    reorderTodos: (draggedTargetId: string, droppedTargetId: string) => void;
    enterTodo: () => void;
}

interface RowAriaProps {
    rowIndex: number;
    listLength: number;
}

export interface TodoListItemProps {
    todo: Todo;

    /**
     * User interaction callbacks
     */
    callbacks: TodoListItemCallbacks;

    /**
     * Gets whether or not the item is being edited
     */
    isBeingEdited: (id: string) => boolean;

    /**
     * Whether the item supports showing the todo item info. This is
     * the content that appears below the todo (e.g. due date, reminder date, list info)
     */
    showTodoListItemInfo: boolean;

    /**
     * Optional list info to display below the todo
     */
    todoListInfo?: TodoListInfo;

    /**
     * Optional class name for the title
     */
    titleClassName?: string;

    /**
     * Optional class name for the complete checkbox
     */
    checkboxClassName?: string;

    /**
     * Optional class name for the todo
     */
    todoClassName?: string;

    /**
     * Optional aria props
     */
    rowAriaProps?: RowAriaProps;
}

export interface TodoListItemHandle {
    setFocus(): void;
}

export default observer(
    function TodoListItem(props: TodoListItemProps, ref: React.Ref<TodoListItemHandle>) {
        const {
            clickEmail,
            markTodoComplete,
            markTodoIncomplete,
            star,
            unstar,
            updateTodoSubject,
            showTodoContextMenu,
            clickTodo,
            updateIsEditing,
        } = props.callbacks;
        const taskItemRef = React.useRef<HTMLDivElement>();
        const focusZoneRef = React.useRef<IFocusZone>();
        const editableTitleRef = React.useRef<IEditableTitle>();

        const isComplete = isTaskComplete(props.todo.Status);
        const isEditing = props.isBeingEdited(props.todo.Id);
        const important = isImportant(props.todo.Importance);

        const onEnterEdit = React.useCallback(() => {
            updateIsEditing(props.todo.Id, true);
        }, [props.todo.Id]);

        const onExitEdit = React.useCallback(() => {
            updateIsEditing(props.todo.Id, false);
        }, [props.todo.Id]);

        const onSaveTitle = React.useCallback(
            (subject: string) => {
                if (props.todo.Subject !== subject) {
                    updateTodoSubject(props.todo.Id, subject);
                }
            },
            [props.todo.Subject, props.todo.Id]
        );

        const setFocus = React.useCallback(() => {
            taskItemRef.current.focus();
        }, []);

        React.useImperativeHandle(ref, () => ({ setFocus }), []);

        const onKeyDown = React.useCallback((target: React.KeyboardEvent<EventTarget>) => {
            switch (target.keyCode) {
                case getRTLSafeKeyCode(KeyCodes.right):
                    focusZoneRef.current.focus();
                    target.preventDefault();
                    target.stopPropagation();
                    break;
            }
        }, []);

        const onTextFieldKeyDown = React.useCallback((target: React.KeyboardEvent<EventTarget>) => {
            switch (target.keyCode) {
                /**
                 * Stop the propogation of the left and right arrow key presses unless the cursor is at the
                 * start or end of the the text so that consuming component's key handlers do not interfere
                 * with the natural behavior of the TextArea.
                 */
                case getRTLSafeKeyCode(KeyCodes.right):
                    if (
                        !editableTitleRef.current.isCursorAtEnd() ||
                        editableTitleRef.current.isTextSelected()
                    ) {
                        // User is interacting with the editable control if:
                        // - cursor is within the text (not at end of text)
                        // - OR text is selected (even if the cursor is at the end of the text)
                        target.stopPropagation();
                    }
                    break;
                case getRTLSafeKeyCode(KeyCodes.left):
                    if (
                        !editableTitleRef.current.isCursorAtStart() ||
                        editableTitleRef.current.isTextSelected()
                    ) {
                        // User is interacting with the editable control if:
                        // - cursor is within the text (not at start of text)
                        // - OR text is selected (even if the cursor is at the start of the text)
                        target.stopPropagation();
                    }
                    break;
                case KeyCodes.down:
                case KeyCodes.up:
                case KeyCodes.home:
                case KeyCodes.end:
                    // Always keep these within editable control
                    target.stopPropagation();
                    break;
            }
        }, []);

        const onClick = React.useCallback(
            (evt: React.MouseEvent<unknown>) => {
                evt.stopPropagation();
                clickTodo(props.todo.Id);
            },
            [props.todo.Id]
        );

        /**
         * The onContextMenu event handler which opens the context menu
         * @param evt the mouse event
         */
        const onItemContextMenu = React.useCallback(
            (evt: React.MouseEvent<HTMLElement>) => {
                evt.stopPropagation();
                evt.preventDefault();
                showTodoContextMenu(props.todo.Id, getAnchorForContextMenu(evt));
            },
            [props.todo.Id]
        );

        const toggleComplete = React.useCallback(() => {
            if (!isComplete) {
                markTodoComplete(props.todo.Id);
            } else {
                markTodoIncomplete(props.todo.Id);
            }
        }, [isComplete, props.todo.Id]);

        const toggleImportant = React.useCallback(() => {
            if (!important) {
                star(props.todo.Id);
            } else {
                unstar(props.todo.Id);
            }
        }, [important, props.todo.Id]);

        let ariaRowLocationLabel = '';
        if (props.rowAriaProps) {
            ariaRowLocationLabel = format(
                loc(taskAriaRowLocationLabel),
                props.rowAriaProps.rowIndex,
                props.rowAriaProps.listLength
            );
        }
        const ariaPropsRow: AriaProperties = {
            role: AriaRoles.row,
            label: format(loc(taskAriaLabel), props.todo.Subject, ariaRowLocationLabel),
        };

        const ariaPropsGridCell: AriaProperties = { role: AriaRoles.gridcell };

        return (
            <div
                ref={taskItemRef}
                className={classNames(styles.taskItem, {
                    [styles.taskItemWithDetails]: isFeatureEnabled('todo-details-view'),
                })}
                onContextMenu={onItemContextMenu}
                onKeyDown={onKeyDown}
                data-is-focusable={true}
                {...generateDomPropertiesForAria(ariaPropsRow)}>
                <FocusZone direction={FocusZoneDirection.horizontal} componentRef={focusZoneRef}>
                    <div
                        className={classNames(styles.body, props.todoClassName, {
                            [styles.isEditing]: isEditing,
                        })}
                        onClick={onClick}>
                        <Icon className={styles.bar1} iconName={ControlIcons.MoreVertical} />
                        <Icon className={styles.bar2} iconName={ControlIcons.MoreVertical} />
                        <div {...generateDomPropertiesForAria(ariaPropsGridCell)}>
                            <CompleteCheckbox
                                isComplete={isComplete}
                                onToggleComplete={toggleComplete}
                                checkboxClassName={props.checkboxClassName}
                            />
                        </div>
                        <div
                            className={styles.titleWrapper}
                            {...generateDomPropertiesForAria(ariaPropsGridCell)}>
                            <EditableTitle
                                ref={editableTitleRef}
                                isEditing={isEditing}
                                title={props.todo.Subject}
                                titleStyle={classNames(
                                    {
                                        [styles.completedTitle]: isComplete,
                                    },
                                    styles.title
                                )}
                                textFieldProps={{
                                    maxLength: 255,
                                }}
                                titleWrapperStyle={props.titleClassName}
                                onExitEdit={onExitEdit}
                                onEnterEdit={onEnterEdit}
                                onSaveTitle={onSaveTitle}
                                moveFocusAway={setFocus}
                                onTextFieldKeyDown={onTextFieldKeyDown}
                            />
                            {props.showTodoListItemInfo && (
                                <TodoListItemInfo
                                    todo={props.todo}
                                    todoListInfo={props.todoListInfo}
                                />
                            )}
                        </div>
                        <div className={styles.charmsContainer}>
                            {props.todo.LinkedEmail && (
                                <div
                                    className={styles.gridCell}
                                    {...generateDomPropertiesForAria(ariaPropsGridCell)}>
                                    <LinkedEmailEntity
                                        emailId={props.todo.LinkedEmail.Id}
                                        onClick={clickEmail}
                                        style={styles.charm}
                                        iconStyle={classNames(
                                            styles.emailCharmIcon,
                                            styles.charmIcon
                                        )}
                                    />
                                </div>
                            )}
                            <div
                                className={styles.gridCell}
                                {...generateDomPropertiesForAria(ariaPropsGridCell)}>
                                <TodoStarCharm
                                    onClick={toggleImportant}
                                    isImportant={important}
                                    style={styles.charm}
                                    iconStyles={styles.charmIcon}
                                />
                            </div>
                        </div>
                    </div>
                </FocusZone>
            </div>
        );
    },
    { forwardRef: true }
);
